import { useState, useEffect } from 'react';
import { API } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Trash2, UserPlus } from 'lucide-react';

export default function SalesmenPage() {
  const [salesmen, setSalesmen] = useState([]);
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchSalesmen = async () => {
    try {
      const res = await API.get('/salesmen');
      setSalesmen(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchSalesmen(); }, []);

  const handleAdd = async () => {
    if (!newName.trim()) { toast.error('Enter salesman name'); return; }
    setLoading(true);
    try {
      await API.post('/salesmen', { name: newName.trim() });
      toast.success(`Salesman "${newName.trim()}" added`);
      setNewName('');
      fetchSalesmen();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add');
    } finally { setLoading(false); }
  };

  const handleDelete = async (salesman) => {
    if (!window.confirm(`Remove salesman "${salesman.name}"?`)) return;
    try {
      await API.delete(`/salesmen/${salesman.id}`);
      toast.success(`Salesman "${salesman.name}" removed`);
      fetchSalesmen();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>Salesmen</h1>
        <p className="hidden sm:block">Manage your salesmen</p>
      </div>

      <Card className="mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Add Salesman</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              placeholder="Salesman name"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
              className="flex-1 sm:max-w-xs"
              data-testid="new-salesman-input"
            />
            <Button onClick={handleAdd} disabled={loading} data-testid="add-salesman-btn">
              <UserPlus size={16} className="mr-2" />Add
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">All Salesmen ({salesmen.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {salesmen.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No salesmen added yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead className="w-16">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {salesmen.map((s, i) => (
                  <TableRow key={s.id} data-testid={`salesman-row-${i}`}>
                    <TableCell className="font-mono text-xs">{i + 1}</TableCell>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(s)} data-testid={`delete-salesman-${i}`}>
                        <Trash2 size={14} className="text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
